<template>
  <div class="tw:max-w-[500px] tw:mx-auto tw:min-h-dvh tw:flex tw:flex-col">
    <div class="tw:relative">
      <nuxt-img
        :src="imageUrl"
        :alt="imageTitle"
        class="tw:w-full"
      />
      <p
        class="tw:absolute! tw:top-10 tw:left-[30px] tw:text-usdg-color-gray-0 tw:font-bold tw:text-[32px]"
      >
        {{ imageTitle }}
      </p>
    </div>

    <div class="tw:px-4">
      <slot name="order-detail" />
      <!-- 실제 주문 데이터 연동 전 placeholder (참고용, 데이터 연동되면 order-detail 슬롯으로 채워서 사용)
      <div>
        <p class="tw:typo-text-s tw:font-medium">
          eSIM <span class="tw:text-usdg-color-gray-400">|</span> 로밍망
        </p>
        <p class="tw:typo-title-xs tw:font-bold">사우디아라비아 4일</p>
        <p class="tw:mt-1 tw:typo-text-m">매일 500MB이후 저속 무제한, 10개</p>
      </div>

      <div class="tw:border-b tw:border-b-[#E5E5EC] tw:mt-3!"></div>

      <div class="tw:mt-4!">
        <div class="tw:flex tw:typo-text-m">
          <p class="tw:w-[80px] tw:text-usdg-color-gray-600">주문번호</p>
          <p>U202606061246633</p>
        </div>
        <div class="tw:flex tw:typo-text-m tw:mt-2!">
          <p class="tw:w-[80px] tw:text-usdg-color-gray-600">결제금액</p>
          <p>32,700원</p>
        </div>
      </div>
      -->

      <CtaButton
        v-if="!hideSubButton && !hideMainButton && !isInsuranceOrderResult"
        size="xl"
        :type="mainBtnType"
        :text="mainButtonText"
        class="tw:mt-4!"
        w-full
        @click-button="emits('click-main-button')"
      />
    </div>

    <div
      v-if="promoBanner"
      class="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:py-[30px] tw:px-4"
    >
      <div class="tw:flex tw:flex-col tw:gap-1 tw:w-full">
        <p class="tw:typo-title-xs tw:font-bold">{{ promoBanner.title }}</p>
        <p class="tw:typo-text-m tw:text-usdg-color-text-body2">{{ promoBanner.subtitle }}</p>
      </div>
      <nuxt-img
        :id="promoBanner.id"
        :src="promoBanner.imageUrl"
        class="tw:w-full tw:aspect-[960/336] tw:rounded-xl tw:object-cover tw:cursor-pointer"
        @click="promoBanner.onClick"
      />
    </div>

    <UsdgBottomCta
      v-if="!isInsuranceOrderResult"
      custom-class="tw:mt-auto!"
    >
      <div
        v-if="hideSubButton"
        class="tw:flex"
      >
        <CtaButton
          size="xl"
          :type="mainBtnType"
          :text="mainButtonText"
          w-full
          @click-button="emits('click-main-button')"
        />
      </div>
      <div
        v-else
        class="tw:flex tw:gap-3"
      >
        <CtaButton
          size="xl"
          type="tertiary"
          :text="subButtonText"
          w-full
          @click-button="handleSubButtonClick"
        />
        <CtaButton
          size="xl"
          text="홈으로 가기"
          w-full
          @click-button="goHome"
        />
      </div>
    </UsdgBottomCta>
  </div>
</template>

<script setup lang="ts">
import { navigateWithHref } from '@/utils/_common/navigation'
import { isNativeShellApp } from '@/utils/appBridgeGate'
const props = withDefaults(
  defineProps<{
    // TODO: orderResult 타입 정의 필요
    orderReulst?: any[]
    imageUrl?: string
    imageTitle?: string
    mainButtonText?: string
    mainBtnType?: string
    hideSubButton?: boolean
    hideMainButton?: boolean // true면 상세 영역 안내 버튼(mainButtonText)을 숨김(하단 고정 버튼만 노출)
    subButtonText?: string // 하단 고정 좌측 버튼 라벨(기본: 내 데이터)
    subButtonTo?: string // 지정 시 좌측 버튼이 내 데이터 대신 이 경로로 이동
    productName?: string // 주문 상품명(국가명) - 프로모션 배너 노출 조건 판단용
    isInsuranceOrderResult?: boolean
    shuttleOrderNo?: string
  }>(),
  {
    orderReulst: [],
    imageTitle: '',
    imageUrl: '',
    mainButtonText: '여행 전날 설치 방법 알림 받기',
    mainBtnType: 'secondary',
    hideSubButton: false,
    hideMainButton: false,
    subButtonText: '내 데이터',
    subButtonTo: undefined,
    productName: '',
    isInsuranceOrderResult: false
  }
)

const emits = defineEmits(['click-main-button'])

const partner = String(useRoute().params.partner || '')

const goHome = () => {
  // 2.2.7 이상만 네이티브 홈. 2.2.6 이하는 openNativeScreen 핸들러가 없어 웹 이동.
  if (isNativeShellApp()) {
    window.flutter_inappwebview?.callHandler('openNativeScreen', { screen: 'home' })
    return
  }

  navigateWithHref('/', partner)
}

const {
  public: { productUrl }
} = useRuntimeConfig()

const goToInsurance = () => {
  window.location.href = productUrl + '/insurance'
}
const goToShuttle = () => {
  window.location.href = productUrl + '/shuttle'
}

// 일본 상품일 때만 1/2 확률로 여행자보험 배너 대신 유심사 TA 배너를 노출
const promoBanner = computed(() => {
  if (!props.productName) return null

  const isJapan = props.productName.includes('일본')
  const showTaBanner = isJapan && Math.random() < 0.5

  if (showTaBanner) {
    return {
      id: 'gtm_shuttle_success_banner',
      title: '일본 공항 이동, 인당 2만 원대부터',
      subtitle: '공항부터 호텔까지, 프라이빗하게.',
      imageUrl: '/img/order/promo/usimsa-ta-banner.webp',
      onClick: goToShuttle
    }
  }

  return {
    id: 'gtm_insur_success_banner',
    title: '2명부터 즉시 할인, 안전 귀국 후 환급까지',
    subtitle: '유심사에서 여행자보험 준비하기',
    imageUrl: '/img/order/promo/travel-insurance-banner.webp',
    onClick: goToInsurance
  }
})

const goToInventory = () => {
  // 2.2.7 이상만 네이티브 내데이터. 2.2.6 이하는 웹 이동.
  if (isNativeShellApp()) {
    window.flutter_inappwebview?.callHandler('openNativeScreen', { screen: 'myData' })
    return
  }

  navigateWithHref('/inventory', partner)
}

const handleSubButtonClick = () => {
  // 네이티브 앱(2.2.7+)은 subButtonTo(웹 폴백 경로)보다 우선 — 지정하지
  // 않으면 subButtonTo 가 있을 때 아래 분기가 죽은 코드가 된다 (TA 결제완료가
  // 항상 subButtonTo 를 같이 넘기던 게 원인). payload 도 반드시
  // {screen, reservationId} 객체여야 한다 — 문자열만 넘기면 앱이 못 읽는다.
  // 계약: usimsa-app-native-mobile docs/handover/WEB_NATIVE_BRIDGE_HANDOVER.md
  if (isNativeShellApp() && props.shuttleOrderNo) {
    window.flutter_inappwebview?.callHandler('openNativeScreen', {
      screen: 'shuttleDetail',
      reservationId: props.shuttleOrderNo
    })
    return
  }

  if (props.subButtonTo) {
    navigateWithHref(props.subButtonTo, partner)
    return
  }

  goToInventory()
}
</script>
